import { useState, useEffect, useMemo } from "react";
import { Ava } from "../atoms/Ava.jsx";

/**
 * UI-3: Admin overview dashboard.
 * Shows aggregate stats across all personas + quick-access cards.
 */
export function AdminDashboard({ personas = [], onSelectPersona, onSetView }) {
  // Aggregate stats from persona data
  const stats = useMemo(() => {
    const total = personas.length;
    const active = personas.filter(p => p.active !== false).length;
    const totalSessions = personas.reduce((s, p) => s + (p.totalSessions ?? 0), 0);
    const totalQuestions = personas.reduce((s, p) => s + (p.totalQuestions ?? 0), 0);
    const totalVoters = personas.reduce((s, p) => s + (p.uniqueVoters ?? 0), 0);
    return { total, active, totalSessions, totalQuestions, totalVoters };
  }, [personas]);

  const topPersonas = useMemo(() =>
    [...personas].sort((a, b) => (b.totalSessions ?? 0) - (a.totalSessions ?? 0)).slice(0, 5),
    [personas]
  );

  const S = {
    card: {
      padding: "20px",
      background: "#0f1623",
      border: "1px solid #1e2d45",
      borderRadius: 12,
      textAlign: "center",
    },
    statValue: {
      fontSize: 28,
      fontWeight: 900,
      color: "#60a5fa",
    },
    statLabel: {
      fontSize: 10,
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginTop: 4,
    },
    personaRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      background: "#0f1623",
      border: "1px solid #1e2d45",
      borderRadius: 10,
      cursor: "pointer",
      transition: "border-color 0.15s",
    },
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900 }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>Admin Console</div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", margin: 0 }}>Dashboard</h1>
      </div>

      {/* Quick stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, marginBottom: 28 }}>
        {[
          ["Personas", stats.total, "#60a5fa"],
          ["Active", stats.active, "#22c55e"],
          ["Sessions", stats.totalSessions.toLocaleString(), "#a78bfa"],
          ["Questions", stats.totalQuestions.toLocaleString(), "#f59e0b"],
          ["Voters", stats.totalVoters.toLocaleString(), "#ec4899"],
        ].map(([label, value, color]) => (
          <div key={label} style={S.card}>
            <div style={{ ...S.statValue, color }}>{value}</div>
            <div style={S.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Top personas by engagement */}
      {topPersonas.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
            Top Personas by Engagement
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {topPersonas.map((p, i) => (
              <div
                key={p.id}
                style={S.personaRow}
                onClick={() => onSelectPersona(p)}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#3b82f6"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "#1e2d45"}
              >
                <span style={{ fontSize: 16, fontWeight: 700, color: "#475569", minWidth: 20 }}>#{i + 1}</span>
                <Ava name={p.name} size={32} color={p.accent_color || p.accentColor} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0" }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{p.role}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#60a5fa" }}>{(p.totalSessions ?? 0).toLocaleString()}</div>
                  <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase" }}>sessions</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#a78bfa" }}>{(p.uniqueVoters ?? 0).toLocaleString()}</div>
                  <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase" }}>voters</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
          Quick Actions
        </h2>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {[
            { icon: "➕", label: "Create Persona", view: "create" },
            { icon: "🎁", label: "Manage Rewards", view: "rewards" },
            { icon: "👥", label: "View All Personas", view: "list" },
          ].map(a => (
            <button
              key={a.view}
              onClick={() => { onSetView(a.view); }}
              style={{
                padding: "14px 20px",
                background: "#0f1623",
                border: "1px solid #1e2d45",
                borderRadius: 10,
                cursor: "pointer",
                color: "#94a3b8",
                fontSize: 13,
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: 8,
                transition: "border-color 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#3b82f6"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "#1e2d45"}
            >
              <span style={{ fontSize: 18 }}>{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
